import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCheckInKeyResult } from "@/hooks/use-key-results";
import { FormError } from "@/components/shared/form-error";
import type { KeyResult } from "@/types/entities";

interface CheckInFormProps {
  keyResult: KeyResult;
}

const percentageSchema = z.object({
  completion_percent: z.coerce.number().min(0).max(100),
});

const metricSchema = z.object({
  current_value: z.coerce.number(),
});

export function CheckInForm({ keyResult }: CheckInFormProps) {
  if (keyResult.status !== "in_progress") {
    return null;
  }

  if (keyResult.type === "binary") {
    return <BinaryCheckIn keyResult={keyResult} />;
  }

  if (keyResult.type === "metric") {
    return <MetricCheckIn keyResult={keyResult} />;
  }

  return <PercentageCheckIn keyResult={keyResult} />;
}

function PercentageCheckIn({ keyResult }: { keyResult: KeyResult }) {
  const checkIn = useCheckInKeyResult(keyResult.id);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(percentageSchema),
    defaultValues: { completion_percent: keyResult.completion_percent },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Check-in</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={handleSubmit((data) => checkIn.mutate(data))}
          className="flex items-end gap-3"
        >
          <div className="flex-1 space-y-2">
            <Label htmlFor="checkin-percent">Completion %</Label>
            <Input
              id="checkin-percent"
              type="number"
              min={0}
              max={100}
              {...register("completion_percent")}
            />
            <FormError message={errors.completion_percent?.message} />
          </div>
          <Button type="submit" size="sm" disabled={checkIn.isPending}>
            {checkIn.isPending ? "Saving..." : "Update"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function MetricCheckIn({ keyResult }: { keyResult: KeyResult }) {
  const checkIn = useCheckInKeyResult(keyResult.id);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(metricSchema),
    defaultValues: { current_value: keyResult.current_value ?? 0 },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Check-in</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={handleSubmit((data) => checkIn.mutate(data))}
          className="flex items-end gap-3"
        >
          <div className="flex-1 space-y-2">
            <Label htmlFor="checkin-value">
              Current Value (target: {keyResult.target_value})
            </Label>
            <Input
              id="checkin-value"
              type="number"
              step="any"
              {...register("current_value")}
            />
            <FormError message={errors.current_value?.message} />
          </div>
          <Button type="submit" size="sm" disabled={checkIn.isPending}>
            {checkIn.isPending ? "Saving..." : "Update"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function BinaryCheckIn({ keyResult }: { keyResult: KeyResult }) {
  const checkIn = useCheckInKeyResult(keyResult.id);
  const [value, setValue] = useState(
    keyResult.completion_percent === 100 ? "100" : "0",
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    checkIn.mutate({ completion_percent: parseInt(value, 10) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Check-in</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex items-end gap-3">
          <div className="flex-1 space-y-2">
            <Label>Status</Label>
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Not Complete</SelectItem>
                <SelectItem value="100">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" size="sm" disabled={checkIn.isPending}>
            {checkIn.isPending ? "Saving..." : "Update"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
